import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  Optional,
  QueryList,
  Self,
} from '@angular/core';
import { FormControl, NgControl } from '@angular/forms';
import { Loading, SelectOption, TemplateDirective } from '@fleet-beat/core';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { debounceTime, delay, filter, map, startWith } from 'rxjs/operators';
import { SimpleField } from '../../abstracts/simple-field';
import { AnexConfig } from '../../models/anex-config.model';
import { GroupedOptions } from '../../models/grouped-options.model';

type ControlValueType = string | number | boolean | object | null;

@Component({
  selector: 'fb-select-autocomplete',
  templateUrl: './select-autocomplete.component.html',
  styleUrls: ['./select-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectAutocompleteComponent extends SimpleField<ControlValueType> implements OnInit {
  @Input()
  override set value(value: ControlValueType) {
    if (this.valueSubject.getValue() !== value) {
      this.valueSubject.next(value);
      if (this.onChanged) {
        this.onChanged(value);
      }
      this.stateChanges.next();
    }
  }
  override get value(): ControlValueType {
    return this.valueSubject.getValue();
  }

  @Input() set options(value: SelectOption[]) {
    if (value) {
      this.optionsSubject.next([
        {
          groupLabel: null,
          options: value,
        },
      ]);

      this.onWriteValue(this.value);
    }
  }

  @Input() set groupedOptions(value: Record<string, SelectOption[]>) {
    if (value) {
      this.optionsSubject.next(
        Object.keys(value).map((key: string) => ({
          groupLabel: key,
          options: value[key],
        })),
      );

      this.onWriteValue(this.value);
    }
  }
  @Input() allowEmpty: boolean = true;
  @Input() prefix: AnexConfig;
  @Input() suffix: AnexConfig;
  @Input() templates: QueryList<TemplateDirective<object>>;
  @Input() loading: Loading = new Loading();
  @Input() noLabel: boolean;

  autocompleteControl: FormControl = new FormControl();
  filteredValues$: Observable<GroupedOptions[]>;

  private optionsSubject: BehaviorSubject<GroupedOptions[]> = new BehaviorSubject(null);
  private searchSubject: BehaviorSubject<string> = new BehaviorSubject('');

  constructor(
    protected override changeDetectorRef: ChangeDetectorRef,
    @Optional() @Self() protected override ngControl: NgControl,
  ) {
    super(changeDetectorRef, ngControl);
  }

  ngOnInit(): void {
    if (!this.value && this.autocompleteControl.value) {
      /* NOTE:
       * here "this.value" is undefined or null, so
       * set "this.value" here is needed to cause in onBlur method set input
       * "this.autocompleteControl.setValue(this.findOptionByValue(this.value));"
       * with non-undefined value, when "this.allowEmpty === false"
       */
      const valueToSet: ControlValueType = this.autocompleteControl.value?.value || undefined;
      this.valueSubject.next(valueToSet);
      this.value = valueToSet;
    }

    this.filteredValues$ = this.getFilteredValues();

    this.autocompleteControl.valueChanges
      .pipe(untilComponentDestroyed(this), startWith(''))
      .subscribe((value: ControlValueType) => {
        if (SelectAutocompleteComponent.isSelectOption(value)) {
          if (this.value !== value) {
            this.value = (value as SelectOption).value;
          }

          this.searchSubject.next('');
          return;
        }

        this.searchSubject.next((value || '').toString());
      });
  }

  onClear(): void {
    this.autocompleteControl.setValue(null, { emitEvent: false });
    this.value = null;
    this.searchSubject.next('');
  }

  onInputBlur(): void {
    of(null)
      .pipe(untilComponentDestroyed(this), delay(200))
      .subscribe(() => {
        if (!this.autocompleteControl.value && this.value && this.allowEmpty) {
          this.value = null;
          return;
        }

        if (
          typeof this.autocompleteControl.value === 'string' &&
          this.displayValue(this.value) !== this.autocompleteControl.value
        ) {
          this.autocompleteControl.setValue(this.findOptionByValue(this.value));
        }
      });
  }

  /* eslint-disable-next-line @typescript-eslint/typedef */
  displayValue = (value: ControlValueType | SelectOption): string => {
    if (!value) {
      return '';
    }

    if (SelectAutocompleteComponent.isSelectOption(value)) {
      return (value as SelectOption).label;
    }

    const option: SelectOption = this.findOptionByValue(value);
    if (option) {
      return option.label;
    }

    return value.toString();
  };

  override writeValue(value: ControlValueType): void {
    this.onWriteValue(value);
    this.changeDetectorRef.markForCheck();
  }

  protected override onWriteValue(value: ControlValueType): void {
    if (!value) {
      this.autocompleteControl.setValue(null, { emitEvent: false });
      return;
    }

    const option: SelectOption = this.findOptionByValue(value);
    if (option === null) {
      this.onClear();
      return;
    }
    this.autocompleteControl.setValue(option ? option : value, { emitEvent: false });
  }

  private getFilteredValues(): Observable<GroupedOptions[]> {
    return combineLatest([this.optionsSubject, this.searchSubject]).pipe(
      debounceTime(1),
      filter(([options]: [GroupedOptions[], string]) => Boolean(options)),
      map(([options, search]: [GroupedOptions[], string]) => {
        search = search.toLowerCase();

        return options
          .map((group: GroupedOptions) => ({
            groupLabel: group.groupLabel,
            options: this.filterOptions(group.options, search),
          }))
          .filter((group: GroupedOptions) => group.options.length > 0);
      }),
      map((groups: GroupedOptions[]) => (groups.length ? groups : [null])),
    );
  }

  private filterOptions(options: SelectOption[], search: string): SelectOption[] {
    if (!search) {
      return options;
    }

    return options.filter((option: SelectOption) => option.label.toLowerCase().indexOf(search) >= 0);
  }

  private findOptionByValue(value: ControlValueType): SelectOption {
    if (value === null || value === undefined) {
      return null;
    }

    const allOptions: SelectOption[] = this.optionsSubject.value?.reduce(
      (result: SelectOption[], curr: GroupedOptions) => [...result, ...curr.options],
      [],
    );
    if (!allOptions) {
      return undefined;
    }

    return allOptions.find((option: SelectOption) => option.value === value) || null;
  }

  private static isSelectOption(value: ControlValueType): boolean {
    return value && Boolean((value as SelectOption).label) && Boolean((value as SelectOption).value);
  }
}
